import * as Yup from 'yup';
import { useNavigate } from "react-router-dom";
import { Spinner } from "react-bootstrap";
import { toast } from "sonner";
import { DEFAULT_DASH_PATH } from "../routes/paths";
import { FormProvider, RHFTextField, RHFAutoComplete, RHFFileUpload, RHFCheckbox } from "../components/Reusable/hook-form";
import { UserAccountState } from '../enums/shared.enums';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { Grid, Button } from '@mui/material';
import { toProperCase } from 'src/utils/Strings';
import { updateProfileApi } from 'src/api';
import { useCallback, useState } from 'react';

export type FormProps = {
  firstName: string;
  lastName: string;
  displayName: string;
  accountType: string;
  state: UserAccountState;
  bio?: string;
  profilePicture?: File;
  isOver18: boolean;
};

const FormSchema = Yup.object().shape({
  firstName: Yup.string().required('First name is required'),
  lastName: Yup.string().required('Last name is required'),
  displayName: Yup.string().required('Display name is required'),
  accountType: Yup.string().required('Account type is required'),
  state: Yup.mixed<UserAccountState>().required('Account state is required'),
  bio: Yup.string().optional(),
  profilePicture: Yup.mixed<File>().optional(),
  isOver18: Yup.boolean().oneOf([true], 'You must be over 18 to use this service').required('Age verification is required'),
});

const DEFAULT_VALUES = {
  firstName: '',
  lastName: '',
  displayName: '',
  accountType: 'artist',
  bio: '',
  state: UserAccountState.ONBOARDED,
  isOver18: false
};

const Onboarding = () => {
  // Navigation
  const navigate = useNavigate();

  // Auth
  // TODO: Uncomment when API is ready.
  // const { isOnboarded } = useAuth();
  // useEffect(() => {
  //   // If the user is already onboarded, redirect to the dashboard.
  //   if (isOnboarded) {
  //     navigate(DEFAULT_DASH_PATH);
  //   }
  // }, [isOnboarded]);



  // SWR Mutate on API call, wait for response

  const [isUpdating, setIsUpdating] = useState(false);

  const updateProfileTrigger = useCallback(async (data: FormProps) => {
    setIsUpdating(true);
    try {
      const response = await updateProfileApi(data);
      if (response instanceof Error) {
        throw response;
      }
      return response;
    } finally {
      setIsUpdating(false);
    }
  }, []);

  
  // Form handlers
  const methods = useForm<FormProps>({
    resolver: yupResolver(FormSchema),
    defaultValues: DEFAULT_VALUES,
  });
  const { handleSubmit } = methods;
  const onSubmit = async (data: FormProps) => {
    
    // Required values are guaranteed.
    // And you can do a lot more cool stuff with hook-form.
    // TODO: Hit cognito API.
    try {
      await updateProfileTrigger(data);
      toast.success('Profile updated successfully!');
      navigate(DEFAULT_DASH_PATH);
    } catch (error) {
      toast.error('Failed to update profile');
      console.error('Error updating profile:', error);
    }
  };



  return (
    <FormProvider methods={methods} onSubmit={handleSubmit(onSubmit)}>
      <main className="equaliq onboarding-page">
        <div className="login-page">
          <section className="login-section">
            <div className="box" style={{ maxWidth: '1000px', margin: '0 auto' }}>
              <div className="onboarding-content" style={{ 
                display: 'grid', 
                gridTemplateColumns: '1fr 2fr',
                gap: '20px',
                margin: '20px 0'
              }}>
                <div className="onboarding-left" style={{ 
                    backgroundColor: '#1E1E1E', 
                    borderRadius: '8px',
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'center',
                    alignItems: 'center',
                    padding: '40px 20px'
                  }}>
                  <div className="onboarding-info">
                    <h4 style={{ 
                      color: 'var(--white80)', 
                      fontSize: '28px',
                      marginBottom: '16px',
                      fontWeight: 600
                    }}>Complete Your Profile</h4>
                    <p style={{ 
                      color: 'var(--white60)', 
                      fontSize: '16px',
                      lineHeight: '1.5'
                    }}>Let's set up your account so you can get the most out of Equal IQ.</p>
                  </div>
                </div>
                <div className="onboarding-right" style={{ 
                    backgroundColor: '#121212',
                    color: 'var(--white80)',
                    borderRadius: '8px',
                    boxShadow: '0 4px 20px rgba(0, 0, 0, 0.25)'
                  }}>
                  <h4 style={{ 
                    color: 'var(--white80)', 
                    fontSize: '24px',
                    marginBottom: '20px',
                    fontWeight: 600
                  }}>
                    Step 3: Complete your profile
                  </h4>

                  <Grid container spacing={2} sx={{ mt: 2 }}>

                    {/* Profile Picture - Moved to top */}
                    <Grid size={12} sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
                      <RHFFileUpload 
                        name="profilePicture"
                        label="Upload Profile Picture"
                      />
                    </Grid>

                    {/* First Name */}
                    <Grid size={6}>
                      <RHFTextField 
                        name="firstName" 
                        placeholder="First Name"
                      />
                    </Grid>

                    {/* Last Name */}
                    <Grid size={6}>
                      <RHFTextField 
                        name="lastName" 
                        placeholder="Last Name"
                      />
                    </Grid>

                    {/* Display name */}
                    <Grid size={12}>
                      <RHFTextField 
                        name="displayName" 
                        placeholder="Display Name"
                      />
                    </Grid>

                    {/* Account type */}
                    <Grid size={12}>
                      <RHFAutoComplete
                        name="accountType"
                        label="What best describes you?"
                        options={['artist', 'manager', 'lawyer', 'producer', 'publisher'] as string[]}
                        isOptionEqualToValue={(option, value) => option === value}
                        getOptionLabel={(option) => toProperCase(option)}
                        fullWidth
                      />
                    </Grid>

                    {/* Bio */}
                    <Grid size={12}>
                      <RHFTextField 
                        name="bio" 
                        placeholder="Tell us a little bit about yourself..."
                      />
                    </Grid>

                    {/* Age verification */}
                    <Grid size={12}>
                      <RHFCheckbox 
                        name="isOver18" 
                        label="I confirm that I am at least 18 years old"
                      />
                    </Grid>

                  </Grid>

                  <Button
                    variant="contained"
                    fullWidth
                    size="large"
                    type="submit"
                    disabled={isUpdating}
                    sx={{
                      mt: 3,
                      mb: 1,
                      backgroundColor: 'primary.main',
                      '&:hover': {
                        backgroundColor: 'primary.dark',
                      },
                      color: '#fff',
                      borderRadius: '8px',
                      padding: '12px 0',
                      fontWeight: 500
                    }}
                  >
                    {isUpdating ? 'Setting Up...' : 'Complete Setup'}
                  </Button>
                </div>
              </div>
            </div>
          </section>
        </div>
      </main>
    </FormProvider>
  );
};

export default Onboarding;